'use client'

import { motion } from 'framer-motion'
import { MapPin, DollarSign, Calendar, Star, Zap, TrendingUp, Search } from 'lucide-react'
import { useState } from 'react'

const jobs = [
  {
    id: 1,
    hospital: 'Stanford Medical Center',
    location: 'Palo Alto, CA',
    specialty: 'ICU',
    rate: 98,
    duration: '13 weeks',
    startDate: 'Dec 15, 2024',
    match: 98,
    trending: true,
    featured: true,
  },
  {
    id: 2,
    hospital: 'Mayo Clinic',
    location: 'Rochester, MN',
    specialty: 'Cardiovascular',
    rate: 102,
    duration: '26 weeks',
    startDate: 'Jan 2, 2025',
    match: 95,
    trending: true,
    featured: true,
  },
  {
    id: 3,
    hospital: 'Johns Hopkins',
    location: 'Baltimore, MD',
    specialty: 'Neuro ICU',
    rate: 96,
    duration: '13 weeks',
    startDate: 'Dec 20, 2024',
    match: 92,
    trending: false,
    featured: true,
  },
  {
    id: 4,
    hospital: 'Cleveland Clinic',
    location: 'Cleveland, OH',
    specialty: 'Emergency',
    rate: 88,
    duration: '13 weeks',
    startDate: 'Jan 10, 2025',
    match: 88,
    trending: false,
    featured: false,
  },
  {
    id: 5,
    hospital: 'UCLA Medical Center',
    location: 'Los Angeles, CA',
    specialty: 'Pediatric ICU',
    rate: 94,
    duration: '13 weeks',
    startDate: 'Dec 28, 2024',
    match: 90,
    trending: true,
    featured: false,
  },
  {
    id: 6,
    hospital: 'Mass General',
    location: 'Boston, MA',
    specialty: 'Trauma',
    rate: 100,
    duration: '26 weeks',
    startDate: 'Jan 5, 2025',
    match: 94,
    trending: false,
    featured: true,
  },
]

export default function Marketplace() {
  const [searchTerm, setSearchTerm] = useState('')

  return (
    <section id="marketplace" className="py-20 bg-gradient-to-b from-gray-900 to-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Live <span className="text-gradient bg-gradient-to-r from-accent-400 to-primary-400">Marketplace</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Direct access to top hospitals. No middlemen, no delays.
            <br />
            Apply with one click, get matched instantly.
          </p>
        </motion.div>

        {/* Search Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-3xl mx-auto mb-12"
        >
          <div className="relative">
            <Search className="absolute left-6 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by location, specialty, or hospital..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-14 pr-6 py-4 bg-white/10 border border-gray-700 rounded-2xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent"
            />
          </div>

          {/* Quick Filters */}
          <div className="flex flex-wrap gap-2 mt-4">
            {['ICU', 'Emergency', 'Cardiovascular', 'California', '$90+/hr', 'Immediate Start'].map((filter, index) => (
              <motion.button
                key={index}
                className="px-4 py-2 glass-effect rounded-full text-sm text-gray-300 hover:bg-white/20 transition-all"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {filter}
              </motion.button>
            ))}
          </div>
        </motion.div>

        {/* Live Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12 max-w-4xl mx-auto"
        >
          {[
            { label: 'Live Positions', value: '2,847', icon: Zap },
            { label: 'Active Nurses', value: '12,340', icon: Star },
            { label: 'Avg. Match Time', value: '4 min', icon: TrendingUp },
            { label: 'Placements Today', value: '156', icon: Calendar },
          ].map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="glass-effect rounded-2xl p-4 text-center"
            >
              <stat.icon className="w-6 h-6 text-accent-400 mx-auto mb-2" />
              <div className="text-2xl font-bold text-white">{stat.value}</div>
              <div className="text-xs text-gray-400">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>

        {/* Job Listings */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {jobs.map((job, index) => (
            <motion.div
              key={job.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              whileHover={{ y: -8, scale: 1.02 }}
              className={`glass-effect rounded-2xl p-6 cursor-pointer group relative overflow-hidden ${
                job.featured ? 'border-2 border-accent-500/50' : ''
              }`}
            >
              {/* Featured Badge */}
              {job.featured && (
                <div className="absolute top-4 right-4 px-3 py-1 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full text-xs text-white font-semibold flex items-center space-x-1">
                  <Star className="w-3 h-3" fill="currentColor" />
                  <span>Featured</span>
                </div>
              )}

              {/* Trending Badge */}
              {job.trending && (
                <div className="absolute top-4 left-4 px-3 py-1 bg-red-500/20 rounded-full text-xs text-red-400 font-semibold flex items-center space-x-1">
                  <TrendingUp className="w-3 h-3" />
                  <span>Hot</span>
                </div>
              )}

              {/* Hospital Info */}
              <div className="mt-8 mb-4">
                <h3 className="text-xl font-bold text-white group-hover:text-gradient group-hover:from-accent-400 group-hover:to-primary-400 transition-all mb-2">
                  {job.hospital}
                </h3>
                <div className="flex items-center space-x-2 text-gray-400 text-sm">
                  <MapPin className="w-4 h-4" />
                  <span>{job.location}</span>
                </div>
              </div>

              {/* Specialty */}
              <div className="inline-block px-3 py-1 bg-purple-500/20 rounded-full text-sm text-purple-300 font-semibold mb-4">
                {job.specialty}
              </div>

              {/* Details */}
              <div className="space-y-3 mb-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-400 text-sm">Rate</span>
                  <div className="flex items-center space-x-1">
                    <DollarSign className="w-4 h-4 text-green-400" />
                    <span className="text-2xl font-bold text-gradient bg-gradient-to-r from-accent-400 to-primary-400">
                      {job.rate}
                    </span>
                    <span className="text-gray-400 text-sm">/hr</span>
                  </div>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400">Duration</span>
                  <span className="text-white font-semibold">{job.duration}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400">Start Date</span>
                  <span className="text-white font-semibold">{job.startDate}</span>
                </div>
              </div>

              {/* AI Match Score */}
              <div className="flex items-center justify-between p-3 bg-green-500/10 border border-green-500/30 rounded-xl mb-4">
                <span className="text-sm text-gray-300">AI Match Score</span>
                <div className="flex items-center space-x-2">
                  <div className="flex items-center space-x-1">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-4 h-4 ${
                          i < Math.floor(job.match / 20) ? 'text-green-400' : 'text-gray-600'
                        }`}
                        fill="currentColor"
                      />
                    ))}
                  </div>
                  <span className="text-lg font-bold text-green-400">{job.match}%</span>
                </div>
              </div>

              {/* Apply Button */}
              <motion.button
                className="w-full px-4 py-3 bg-gradient-to-r from-accent-500 to-primary-500 rounded-xl text-white font-semibold opacity-0 group-hover:opacity-100 transition-opacity"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Quick Apply
              </motion.button>
            </motion.div>
          ))}
        </div>

        {/* Load More */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <motion.button
            className="px-8 py-4 glass-effect rounded-full text-white font-semibold hover:bg-white/20 transition-all"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Load More Opportunities
          </motion.button>
        </motion.div>
      </div>
    </section>
  )
}
